import { currencyFormatter } from '@/helpers/currencyFormatter';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { PayoutTransactionClientData } from '@/types/Payouts';
import { Box, BoxProps, Icon } from '@palmetto/palmetto-components';

import { AccountDocument, PayeeType, PayoutEvent, ProjectDocument } from 'types';

import { HoverOnlyTooltipComponent } from '../Tooltip';

export const SubtitleBox = ({ children }: { children: any }) => (
  <Box as="h3" color="grey-500" fontWeight={'bold'} padding={'0 0 0 0'} display={'inline-block'}>
    {children}
  </Box>
);

export const SmallText = ({ children }: { children: any }) => (
  <Box as="span" color="grey-600" fontSize="2xs" fontWeight="regular">
    {children}
  </Box>
);

export const RightAmountBox = ({
  title,
  amount,
  tooltipText,
  containerStyle,
}: {
  title: string;
  amount: string;
  tooltipText?: string;
  containerStyle?: BoxProps;
}) => (
  <Box alignItems="flex-end" direction="row" justifyContent="space-between" {...containerStyle}>
    {tooltipText ? (
      <HoverOnlyTooltipComponent
        content={tooltipText}
        onClose={() => {}}
        displayedContainerStyle={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        contentStyle={{
          fontWeight: 'regular',
          borderColor: 'grey-400',
          borderWidth: 'xs',
        }}
      >
        <Box color="grey-500" fontSize="xs" fontWeight="medium" display="inline-flex" alignItems="center" gap="2xs">
          {title}
        </Box>
      </HoverOnlyTooltipComponent>
    ) : (
      <Box color="grey-500" fontSize="xs" fontWeight="bold">
        {title}
      </Box>
    )}
    <Box color={tooltipText ? 'grey-400' : 'grey-700'} fontSize="sm" fontWeight={tooltipText ? 'regular' : 'bold'}>
      {amount}
    </Box>
  </Box>
);

export const LeftAmountBox = ({ amount, percent }: { amount: string; percent?: string }) => (
  <Box
    color="grey-700"
    fontSize="xs"
    fontWeight="medium"
    style={{
      flex: 1,
    }}
  >
    {amount}
    <br />
    {percent && (
      <Box as="span" color="grey-400" fontSize="xs" padding="2xs 0 0 0" fontWeight="medium">
        {percent}
      </Box>
    )}
  </Box>
);

export const LeftContainerBox = ({ children }: { children: any }) => (
  <Box
    margin="0 sm 0 0"
    style={{
      flex: '1',
      paddingBottom: '2px',
    }}
  >
    {children}
  </Box>
);

export const LeftAmountParentBox = ({
  amount,
  eventPercent,
  title,
  checkbox,
}: {
  amount: number;
  eventPercent?: number;
  title: string;
  checkbox?: boolean;
}) => (
  <LeftContainerBox>
    <Box
      direction="row"
      style={{
        minHeight: '3.5em',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SubtitleBox>{title}</SubtitleBox>
      {checkbox ? (
        <Box margin="0 0 0 xs">
          <Icon name="check" color="success" />
        </Box>
      ) : null}
    </Box>

    <Box borderColor="separator" borderWidth="xs 0 0 0" padding="xs 0 0 0">
      <LeftAmountBox
        amount={currencyFormatter.format(amount)}
        percent={eventPercent ? percentageFormatter.format(eventPercent) : undefined}
      />
    </Box>
  </LeftContainerBox>
);

export interface AccountFundingSummaryProps {
  project?: ProjectDocument;
  transactions?: PayoutTransactionClientData[];
}

export interface AccountFundingSummaryComponentProps {
  account: AccountDocument;
}

export const useGetPaymentPlanSummary = (summary: any) => {
  const elements: {
    component: any;
    amount: number;
    id: PayoutEvent | PayeeType;
  }[] = [];

  summary?.paymentSchedule?.forEach((scheduleItem: any) =>
    elements.push({
      component: (
        <LeftAmountParentBox
          amount={scheduleItem.amount}
          eventPercent={scheduleItem.paymentPercent}
          title={scheduleItem.event}
          checkbox={scheduleItem.amount >= scheduleItem.expectedAmount}
        />
      ),
      amount: scheduleItem.amount,
      id: scheduleItem.event,
    }),
  );

  if (summary?.directPay !== undefined) {
    elements.push({
      component: <LeftAmountParentBox amount={summary.directPay} title="Vendor Direct Pay" checkbox={false} />,
      amount: summary.directPay,
      id: PayeeType.vendor,
    });
  }

  return elements;
};
